import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { faAngleDown, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useKanbanBoardRowStyles } from 'components/UI/V2/KanbanBoard/Row/KanbanBoardRow.styles';
import React from 'react';

interface KanbanBoardRowProps {
    id: string;
    title: string;
    count?: number;
    columnId: string;
    headerStart?: React.ReactNode;
    headerEnd?: React.ReactNode;
    acceptsDrop?: boolean;
    children?: React.ReactNode;
}

export const KanbanBoardRow: React.FC<KanbanBoardRowProps> = ({
    id,
    title,
    count,
    children,
    columnId,
    headerStart,
    headerEnd,
    acceptsDrop = true
}) => {
    const classes = useKanbanBoardRowStyles();

    const contentRef = React.useRef<HTMLDivElement>(null);

    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [isDropTarget, setIsDropTarget] = React.useState(false);

    const hasChildren = React.useMemo(() => {
        return React.Children.count(children) > 0;
    }, [children]);

    React.useEffect(() => {
        if (React.Children.count(children) === 0) {
            setIsCollapsed(true);
        }
        // Only the initial empty state collapses the unit. A status filter can hide every card without closing the header.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const element = contentRef.current;
        if (!element || !acceptsDrop) return;

        return dropTargetForElements({
            element,
            getData: () => ({
                type: 'row',
                rowId: id,
                columnId: columnId
            }),
            canDrop: ({ source }) => {
                return source.data.type === 'kanban-card';
            },
            onDragEnter: () => {
                setIsDropTarget(true);
                setIsCollapsed(false);
            },
            onDragLeave: () => {
                setIsDropTarget(false);
            },
            onDrop: () => {
                setIsDropTarget(false);
            }
        });
    }, [id, columnId, acceptsDrop]);

    return (
        <div
            ref={contentRef}
            data-row-id={id}
            className={classes.root}
        >
            <div
                className={classes.header}
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className={classes.leading}>
                    {headerStart}
                    <div className={classes.title}>
                        <span className={classes.titleText}>{title}</span>

                        {count !== undefined && <span className={classes.counter}>{count}</span>}
                    </div>
                </div>

                <div className={classes.trailing}>
                    {headerEnd}
                    <FontAwesomeIcon icon={isCollapsed ? faAngleRight : faAngleDown} />
                </div>
            </div>

            {!isCollapsed && (
                <div
                    data-row-content={id}
                    data-has-children={hasChildren}
                    data-drop-target={isDropTarget}
                    className={classes.content}
                >
                    {children}
                </div>
            )}
        </div>
    );
};
